import { useState, useEffect } from "react";
import { registerAndSendVerification, checkVerificationAndDelete } from "@auth/auth";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputField, Button } from "../common";

const registerSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export const Register = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(registerSchema),
  });

  const handleRegister = async (data) => {
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const { message } = await registerAndSendVerification(data.email, data.name, data.password);
      setSuccessMessage(message);

      setTimeout(async () => {
        await checkVerificationAndDelete();
      }, 120000);

      setTimeout(() => {
        navigate("/login");
      }, 5000);
    } catch (error) {
      setErrorMessage(`An error occurred: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center px-4"
      style={{ backgroundImage: "url('/bg/bghero5.jpg')" }}
    >
      <div className="fixed top-0 left-0 w-full h-full bg-cyan-900 bg-opacity-40 backdrop-blur-sm"></div>
      <motion.div
        className="relative z-0 max-w-4xl w-full mx-auto bg-white bg-opacity-40 shadow-lg backdrop-blur-md grid md:grid-cols-2 rounded-xl overflow-hidden"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
      >
        <div
          className="hidden md:flex flex-col justify-center text-white bg-cover bg-center p-8 h-full relative rounded-tl-xl rounded-bl-xl"
          style={{ backgroundImage: "url('/bg/bghero1.jpg')" }}
        >
          <div className="absolute inset-0 bg-cyan-900 bg-opacity-80 backdrop-blur-sm z-0 rounded-tl-xl rounded-bl-xl"></div>

          <motion.h1
            className="text-4xl font-bold mb-4 drop-shadow-md z-0 relative"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Welcome
          </motion.h1>
          <motion.p
            className="text-lg drop-shadow-md z-20 relative"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.3 }}
          >
            Sign in to access your account and enjoy the best experience with us.
          </motion.p>
        </div>

        <div className="flex flex-col p-6 z-10">
          <p className="text-2xl font-semibold mb-6 text-center text-white">
            Create Account
          </p>
          <form
            onSubmit={handleSubmit(handleRegister)}
            className="flex flex-col gap-4 text-black"
          >
            <InputField
              label="Name"
              name="name"
              type="text"
              placeholder="Full Name"
              {...register("name")}
              error={errors.name?.message}
              labelColor="text-white"
            />

            <InputField
              label="Email"
              name="email"
              type="email"
              placeholder="Email"
              {...register("email")}
              error={errors.email?.message}
              labelColor="text-white"
            />

            <InputField
              label="Password"
              name="password"
              type="password"
              placeholder="Password"
              {...register("password")}
              error={errors.password?.message}
              labelColor="text-white"
            />

            {errorMessage && (
              <p className="text-sm text-white text-center -mt-2">
                {errorMessage}
              </p>
            )}
            {successMessage && (
              <p className="text-sm text-white text-center -mt-2">
                {successMessage}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              className="mt-4 w-full h-10"
              disabled={isSubmitting || loading}
            >
              {isSubmitting || loading ? "Processing..." : "Create Account"}
            </Button>
          </form>

          <div className="text-center mt-4 text-white">
            <p>
              Already have an account?{" "}
              <Link to="/login" className="text-cyan-800 hover:underline">
                Log in here
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
